import { createTickToBarAggregator } from '@wiki/kline/components/datafeed'
import type {
  DatafeedAdapter,
  DatafeedBar,
  DatafeedSubscriptionResult,
  DatafeedSymbolInfo,
  DatafeedTick,
  GetBarsParams,
  GetBarsResult,
  SubscribeBarsParams,
} from '@wiki/kline/types'
import {
  fetchKlines,
  peekCachedInitConfig,
  pruneExpiredLocalCache,
  type BrokerSymbol,
  type Period,
} from '~/api/wikitrade-market'
import { formatSymbolName } from './utils'

export interface WikiTradeDatafeedAdapterOptions {
  baseURL: string
  getAccessToken: () => string
  getActiveSymbol?: () => string
  getLanguageCode?: () => string
  /** 页面已加载的品种元数据；有则优先使用，避免 resolveSymbol 等待全量 initconfig。 */
  getBrokerSymbol?: (symbol: string) => BrokerSymbol | undefined
  subscribeRealtimeBars?: (params: SubscribeBarsParams, listener: (bar: DatafeedBar) => void) => () => void
  onRealtimeUpdate?: (bar: DatafeedBar) => void
  onLoadingStatusChange?: (loading: boolean) => void
  onDataReplaced?: (bars: DatafeedBar[]) => void
  onError?: (error: unknown) => void
}

export interface WikiTradeDatafeedAdapter extends DatafeedAdapter {
  destroy(): void
}

type WikiTradeRealtimeBar = DatafeedBar & { marketTick?: boolean; barTime?: number; period?: string }
type WikiTradeRealtimeTick = DatafeedTick & { barTime?: number; period?: string }

export interface TransactionTimeConfig {
  timezone: string
  transaction_time_week: Array<{
    week_id: number
    list: Array<{ start_time: string; end_time: string }>
  }>
}

interface StoredWsToken {
  cacheKey: string
  token: string
}

const WS_TOKEN_STORAGE_KEY = 'wikitrade.websocket.token.v2'
const WS_TOKEN_TTL_MS = 30 * 60 * 1000

const canUseStorage = () => typeof window !== 'undefined' && Boolean(window.localStorage)

const readLocalCache = <T>(key: string, options: { ttlMs?: number } = {}): T | null => {
  if (!canUseStorage()) return null

  try {
    const text = window.localStorage.getItem(key)
    if (!text) return null
    const payload = JSON.parse(text) as { value: T; expiresAt?: number; createdAt?: number }
    const expiresAt = payload.expiresAt || (payload.createdAt && options.ttlMs ? payload.createdAt + options.ttlMs : 0)

    if (expiresAt && expiresAt <= Date.now()) {
      window.localStorage.removeItem(key)
      return null
    }

    return payload.value
  } catch {
    window.localStorage.removeItem(key)
    return null
  }
}

// localStorage 只用于减少重复加密，写入失败（例如配额超限）不能影响 WebSocket 鉴权流程。
const writeLocalCache = <T>(key: string, value: T, options: { ttlMs?: number } = {}) => {
  if (!canUseStorage()) return

  const now = Date.now()
  const text = JSON.stringify({
    value,
    createdAt: now,
    expiresAt: options.ttlMs ? now + options.ttlMs : undefined,
  })

  try {
    window.localStorage.setItem(key, text)
    return
  } catch {
    // 配额不足时先回收行情页的过期缓存，再重试一次。
    pruneExpiredLocalCache()
  }

  try {
    window.localStorage.setItem(key, text)
  } catch {
    // 仍然写入失败则放弃本次缓存，token 照常返回给调用方。
  }
}

const parseTransactionTime = (value: string) => {
  const [hours, minutes] = value.split(':').map(Number)
  if (
    !Number.isInteger(hours) ||
    !Number.isInteger(minutes) ||
    hours! < 0 ||
    hours! > 23 ||
    minutes! < 0 ||
    minutes! > 59
  ) {
    return undefined
  }
  return hours! * 60 + minutes!
}

const getTransactionTimezoneOffset = (timezone: string) => {
  const match = timezone.match(/^([+-])(\d{2}):?(\d{2})?$/)
  if (!match) return 0
  const offset = Number(match[2]) * 60 + Number(match[3] || 0)
  return match[1] === '+' ? offset : -offset
}

/** 无有效交易时段配置时返回 undefined。 */
export const getTransactionTimeTradingStatus = (
  transactionTime: unknown,
  now = Date.now(),
): 'open' | 'closed' | undefined => {
  if (!transactionTime || typeof transactionTime !== 'object') return undefined
  const config = transactionTime as Partial<TransactionTimeConfig>
  if (typeof config.timezone !== 'string' || !Array.isArray(config.transaction_time_week)) return undefined

  const localNow = new Date(now + getTransactionTimezoneOffset(config.timezone) * 60_000)
  const weekday = localNow.getUTCDay()
  const minute = localNow.getUTCHours() * 60 + localNow.getUTCMinutes()
  const sessionsByWeekday = new Map<number, Array<{ start: number; end: number }>>()

  for (const day of config.transaction_time_week) {
    if (!day || !Number.isInteger(day.week_id) || !Array.isArray(day.list)) continue
    const sessions = day.list.flatMap((range) => {
      const start = parseTransactionTime(range.start_time)
      const end = parseTransactionTime(range.end_time)
      return start === undefined || end === undefined ? [] : [{ start, end: end === 23 * 60 + 59 ? 24 * 60 : end }]
    })
    if (sessions.length) sessionsByWeekday.set(day.week_id === 7 ? 0 : day.week_id, sessions)
  }
  if (!sessionsByWeekday.size) return undefined

  const isOpen = (sessions: Array<{ start: number; end: number }> | undefined, fromPreviousDay = false) =>
    sessions?.some(({ start, end }) =>
      start === end
        ? !fromPreviousDay
        : start < end
          ? !fromPreviousDay && minute >= start && minute < end
          : fromPreviousDay
            ? minute < end
            : minute >= start,
    ) || false

  return isOpen(sessionsByWeekday.get(weekday)) || isOpen(sessionsByWeekday.get((weekday + 6) % 7), true)
    ? 'open'
    : 'closed'
}

const isHistoryBoundaryError = (error: unknown) => {
  return error instanceof Error && /请求日期超出可用历史数据/.test(error.message)
}

export const parseTransactionTimeToSession = (transactionTimeConfig: TransactionTimeConfig) => {
  const { timezone: rawTimezone, transaction_time_week: weekList } = transactionTimeConfig
  let timezone = 'Etc/UTC'
  if (rawTimezone === '+08:00' || rawTimezone === '+08') {
    timezone = 'Asia/Shanghai'
  } else if (rawTimezone !== '+00:00' && rawTimezone !== '+00') {
    const match = rawTimezone.match(/^([+-])(\d{2}):?(\d{2})?$/)
    if (match) {
      timezone = `${match[1]}${match[2]}:${match[3] || '00'}`
    } else {
      try {
        // 构造一次格式化器用于校验时区标识，非法标识会在构造阶段抛错。
        Intl.DateTimeFormat('en-US', { timeZone: rawTimezone }).format()
        timezone = rawTimezone
      } catch {
        // 无法识别时区标识时回退 UTC。
      }
    }
  }

  const activeWeekdays = new Set<number>()
  const sessionsByDay: Record<number, string[]> = {}
  if (Array.isArray(weekList)) {
    weekList.forEach((dayItem) => {
      const weekday = dayItem.week_id === 7 ? 0 : dayItem.week_id
      const list = dayItem.list || []
      if (!list.length) return
      activeWeekdays.add(weekday)
      sessionsByDay[weekday] = list.map((range) => {
        const start = range.start_time.replace(/:/g, '').substring(0, 4)
        const rawEnd = range.end_time.replace(/:/g, '').substring(0, 4)
        return `${start}-${rawEnd === '2359' ? '2400' : rawEnd}`
      })
    })
  }

  const weekend = Array.from({ length: 7 }, (_, weekday) => weekday).filter((weekday) => !activeWeekdays.has(weekday))
  const regular = [...new Set(Object.values(sessionsByDay).flat())].join(',') || '0000-2400'
  return {
    timezone,
    session: { timezone, regular },
    calendar: { timezone, weekend },
  }
}

/** 行情 WebSocket 要求用 AES-CBC 生成 URL-safe token；缓存 30 分钟即可覆盖多数页面会话。 */
export const encryptSocketToken = async (pid: string, accountId: string) => {
  const cacheKey = `${pid}:${accountId}`
  const cached = readLocalCache<StoredWsToken>(WS_TOKEN_STORAGE_KEY, { ttlMs: WS_TOKEN_TTL_MS })
  if (cached?.cacheKey === cacheKey && cached.token) return cached.token

  // 兜底：非安全上下文下 crypto.subtle 不可用时，对默认 pid=502 使用预计算 token。
  const isDefaultConfig = pid === '502' && !accountId
  const hasSubtleCrypto = typeof crypto !== 'undefined' && typeof crypto.subtle !== 'undefined'

  if (!hasSubtleCrypto && isDefaultConfig) {
    const token = 'dzVNENEZIr3jmHBj4_1sbA'
    writeLocalCache(WS_TOKEN_STORAGE_KEY, { cacheKey, token }, { ttlMs: WS_TOKEN_TTL_MS })
    return token
  }

  const text = `${pid}@${accountId}@0`
  const keyStr = '1234567890123456'
  const ivStr = 'I Love Go Frame!'
  const encoder = new TextEncoder()
  const data = encoder.encode(text)
  const keyData = encoder.encode(keyStr)
  const ivData = encoder.encode(ivStr)

  try {
    const cryptoKey = await crypto.subtle.importKey('raw', keyData, { name: 'AES-CBC' }, false, ['encrypt'])
    const encryptedBuffer = await crypto.subtle.encrypt({ name: 'AES-CBC', iv: ivData }, cryptoKey, data)
    const base64 = btoa(String.fromCharCode(...new Uint8Array(encryptedBuffer)))
    const token = base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')

    writeLocalCache(WS_TOKEN_STORAGE_KEY, { cacheKey, token }, { ttlMs: WS_TOKEN_TTL_MS })
    return token
  } catch (error) {
    if (!hasSubtleCrypto) {
      throw new Error(
        'Web Crypto API (crypto.subtle) is not supported in this environment (likely due to insecure HTTP context).',
        { cause: error },
      )
    }
    throw error
  }
}

/** 生成实时订阅缓存 key，用分隔符避免 symbol 与 resolution 直接拼接产生歧义。 */
const getRealtimeKey = (symbol: string, resolution: string) => `${symbol}\u0001${resolution}`

/** 适配 @wiki/kline-vue v2 datafeed；历史走 fetchKlines，实时由外部 QuoteSocket 注入。 */
export const createWikiTradeDatafeedAdapter = (options: WikiTradeDatafeedAdapterOptions): WikiTradeDatafeedAdapter => {
  const realtimeSubscriptions = new Map<string, () => void>()
  const latestBars = new Map<string, DatafeedBar>()
  let subscriberSequence = 0
  let lastPageRequestId = 0

  return {
    async resolveSymbol(symbol: string): Promise<DatafeedSymbolInfo> {
      // 出图优先：不 await 全量 initconfig，先用页面态/本地缓存/默认值 resolve，让 getBars 立刻发出。
      // initconfig 与语言无关，本地缓存为全局单份。
      const brokerSymbol =
        options.getBrokerSymbol?.(symbol) || peekCachedInitConfig()?.find((item) => item.symbol === symbol)
      const transactionTime = brokerSymbol?.transaction_time as TransactionTimeConfig | undefined
      const sessionConfig =
        transactionTime && typeof transactionTime === 'object' && Array.isArray(transactionTime.transaction_time_week)
          ? parseTransactionTimeToSession(transactionTime)
          : {}
      return {
        symbol: brokerSymbol?.symbol || symbol,
        name: brokerSymbol ? formatSymbolName(brokerSymbol) : symbol,
        timezone: 'Asia/Shanghai',
        pricePrecision: brokerSymbol?.price_precision ?? 2,
        supportedResolutions: ['1m', '5m', '15m', '30m', '1h', '4h', '1d', '1w', '1M'],
        ...sessionConfig,
      }
    },

    async getBars(params: GetBarsParams): Promise<GetBarsResult> {
      const accessToken = options.getAccessToken()
      const activeSymbol = options.getActiveSymbol?.()
      // 分页保留已有图表，不用页面 loading 遮盖。
      const shouldSyncPageState =
        (!activeSymbol || params.symbol === activeSymbol) && !Number.isFinite(params.beforeTime)
      const pageRequestId = shouldSyncPageState ? ++lastPageRequestId : 0
      if (!accessToken) return { bars: [], hasMoreBefore: false }

      try {
        if (shouldSyncPageState) options.onLoadingStatusChange?.(true)
        const queryBeforeTime = Number.isFinite(params.beforeTime)
          ? params.beforeTime
          : Number.isFinite(params.to)
            ? (params.to as number) + 1
            : undefined
        const fetchedBars = await fetchKlines(
          options.baseURL,
          params.symbol,
          params.resolution as Period,
          accessToken,
          {
            beforeTime: queryBeforeTime,
            limit: params.countBack,
            languageCode: options.getLanguageCode?.(),
          },
        )
        const hasBeforeTime = Number.isFinite(params.beforeTime)
        const bars = hasBeforeTime ? fetchedBars.filter((bar) => bar.time < (params.beforeTime as number)) : fetchedBars
        const latestBar = bars.at(-1)
        const realtimeKey = getRealtimeKey(params.symbol, params.resolution)
        if (latestBar && (!latestBars.has(realtimeKey) || latestBar.time >= latestBars.get(realtimeKey)!.time)) {
          latestBars.set(realtimeKey, latestBar)
        }
        const nextBeforeTime = bars[0]?.time
        const cursorMoved =
          !hasBeforeTime ||
          (Number.isFinite(nextBeforeTime) && (nextBeforeTime as number) < (params.beforeTime as number))
        const requestedCount = Number.isFinite(params.countBack) ? Math.ceil(params.countBack as number) : undefined

        if (shouldSyncPageState && pageRequestId === lastPageRequestId) {
          if (!params.beforeTime) options.onDataReplaced?.(bars)
          options.onLoadingStatusChange?.(false)
        }
        return {
          bars,
          hasMoreBefore: Boolean(cursorMoved && requestedCount !== undefined && bars.length >= requestedCount),
          nextBeforeTime,
        }
      } catch (error) {
        if (Number.isFinite(params.beforeTime) && isHistoryBoundaryError(error)) {
          if (shouldSyncPageState && pageRequestId === lastPageRequestId) options.onLoadingStatusChange?.(false)
          return { bars: [], hasMoreBefore: false }
        }
        if (shouldSyncPageState && pageRequestId === lastPageRequestId) {
          options.onLoadingStatusChange?.(false)
          options.onError?.(error)
        }
        throw error
      }
    },

    subscribeBars(params: SubscribeBarsParams, onBar: (bar: DatafeedBar) => void): DatafeedSubscriptionResult {
      const id = `${params.symbol}:${params.resolution}:${++subscriberSequence}`
      const realtimeKey = getRealtimeKey(params.symbol, params.resolution)
      const seedBarTime = latestBars.get(realtimeKey)?.time
      const aggregator = createTickToBarAggregator({
        resolution: params.resolution,
        timezone: params.timezone,
        session: params.session,
        seedBars: latestBars.has(realtimeKey) ? [latestBars.get(realtimeKey)!] : [],
        maxBars: 2,
        alignTime: (tick) => {
          const realtimeTick = tick as WikiTradeRealtimeTick
          return realtimeTick.period === params.resolution ||
            (!realtimeTick.period && realtimeTick.barTime === seedBarTime)
            ? realtimeTick.barTime
            : undefined
        },
      })
      const unsubscribe = options.subscribeRealtimeBars?.(params, (bar) => {
        const realtimeBar = bar as WikiTradeRealtimeBar
        const tick: WikiTradeRealtimeTick = {
          time: bar.time,
          price: bar.close,
          barTime: realtimeBar.barTime,
          period: realtimeBar.period,
        }
        const update = realtimeBar.marketTick ? aggregator.pushTick(tick) : aggregator.pushBar(bar)
        if (!update) return
        latestBars.set(realtimeKey, update.bar)
        onBar(update.bar)
        const activeSymbol = options.getActiveSymbol?.()
        if (!activeSymbol || params.symbol === activeSymbol) options.onRealtimeUpdate?.(update.bar)
      })
      if (unsubscribe) realtimeSubscriptions.set(id, unsubscribe)
      return {
        id,
        unsubscribe: () => {
          realtimeSubscriptions.get(id)?.()
          realtimeSubscriptions.delete(id)
        },
      }
    },

    unsubscribeBars(subscriberId: string) {
      realtimeSubscriptions.get(subscriberId)?.()
      realtimeSubscriptions.delete(subscriberId)
    },

    destroy() {
      realtimeSubscriptions.forEach((unsubscribe) => unsubscribe())
      realtimeSubscriptions.clear()
    },
  }
}
